/**
 * Which month you are looking at, and the fact that it might not be the live one.
 *
 * This is the most consequential control in the top bar and the easiest to under-build. A
 * governance product lets people look at a closed month — that is most of what a governance
 * product is for — and a closed month is a *sealed snapshot*: it cannot be corrected, and
 * anything that looks editable in it is a lie.
 *
 * So the control does three things:
 *
 *   1. Names the period, always. Never "current" — an unqualified figure is how a sealed
 *      number and a live one end up side by side with nothing to tell them apart.
 *   2. Distinguishes open from sealed by shape as well as colour: a breathing dot for the
 *      open period, a seal glyph for a closed one. The two greens are not enough on a
 *      projector, and this gets projected.
 *   3. Puts the way back one click away. Someone who time-travelled and forgot is the
 *      person most likely to file a bug about the product being read-only.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '@/api/client'
import { navigate } from '@/app/routes'
import { useViewPeriod } from '@/app/viewPeriod'
import { Icon } from '@/components/Icon'
import { formatMonth } from '@/lib/format'

export type PeriodView = {
  code: string
  data_date: string
  status: string
  opened_at: string | null
  closed_at: string | null
  snapshot_row_count: number | null
  packages_expected: number | null
  packages_reported: number | null
}

export function PeriodSwitcher() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const { code, setCode } = useViewPeriod()

  const periods = useQuery({
    queryKey: ['periods'],
    queryFn: ({ signal }) => api.get<PeriodView[]>('/periods?limit=24', signal),
    // Periods change once a month. Anything shorter is a request that will never find news.
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (!open) return
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const all = periods.data ?? []
  const live = all.find((period) => period.status === 'open') ?? null
  const viewing = (code ? all.find((period) => period.code === code) : null) ?? live
  const sealed = viewing?.status === 'closed'

  if (!viewing) {
    // No periods yet — a tenant provisioned this morning. Saying so beats an empty control
    // that reads as a failed request.
    return (
      <span className="period period--none" data-testid="period">
        {t('period.none')}
      </span>
    )
  }

  return (
    <div className="period__wrap" ref={box}>
      <button
        type="button"
        className="period"
        data-state={sealed ? 'sealed' : 'open'}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        data-testid="period"
      >
        {sealed ? (
          <Icon name="seal" size={13} strokeWidth={2} className="period__seal" />
        ) : (
          <span className="period__live" aria-hidden="true" />
        )}
        <span className="period__code">{formatMonth(viewing.code, i18n.language)}</span>
        <span className="period__sep" aria-hidden="true">
          ·
        </span>
        <span className="period__state">{t(`period.${viewing.status}`, viewing.status)}</span>
        <Icon name="chevronDown" size={13} strokeWidth={2} className="period__caret" />
      </button>

      {open ? (
        <div className="menu" role="menu" data-testid="period-menu">
          <p className="menu__title">{t('period.timeTravel')}</p>
          <div className="menu__scroll" data-scroll="1">
            {all.map((period) => (
              <button
                key={period.code}
                type="button"
                role="menuitem"
                className="menu__item"
                aria-current={period.code === viewing.code ? 'true' : undefined}
                onClick={() => {
                  setCode(period.status === 'open' ? null : period.code)
                  setOpen(false)
                }}
              >
                <span className="menu__mark" data-state={period.status}>
                  <Icon
                    name={period.status === 'closed' ? 'seal' : 'clock'}
                    size={12}
                    strokeWidth={2.4}
                  />
                </span>
                <span className="menu__body">
                  <span className="menu__label">{formatMonth(period.code, i18n.language)}</span>
                  <span className="menu__sub">
                    {period.status === 'closed'
                      ? t('period.snapshots', { count: period.snapshot_row_count ?? 0 })
                      : t(`period.${period.status}`, period.status)}
                  </span>
                </span>
                {period.code === viewing.code ? (
                  <Icon name="check" size={14} strokeWidth={2.6} className="menu__check" />
                ) : null}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="menu__foot"
            onClick={() => {
              setOpen(false)
              navigate({ name: 'periods' })
            }}
          >
            {t('period.openCloseCentre')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The banner that sits above every screen while you are looking at a sealed month.
 *
 * Deliberately unmissable and deliberately not a modal. Someone reading a closed period is
 * doing something legitimate; they just need to be unable to forget it.
 */
export function SnapshotBanner() {
  const { t, i18n } = useTranslation()
  const { code, setCode } = useViewPeriod()

  const periods = useQuery({
    queryKey: ['periods'],
    queryFn: ({ signal }) => api.get<PeriodView[]>('/periods?limit=24', signal),
    staleTime: 5 * 60_000,
  })

  if (!code) return null
  const all = periods.data ?? []
  const viewing = all.find((period) => period.code === code)
  if (!viewing || viewing.status !== 'closed') return null
  const live = all.find((period) => period.status === 'open')

  return (
    <p className="banner banner--snapshot" role="status" data-testid="snapshot-banner">
      <Icon name="seal" size={16} strokeWidth={1.8} />
      <span className="banner__text">
        <strong>{formatMonth(viewing.code, i18n.language)}</strong> — {t('period.sealedNote')}
      </span>
      {live ? (
        <button type="button" className="banner__action" onClick={() => setCode(null)}>
          {t('period.backToOpen', { month: formatMonth(live.code, i18n.language) })}
        </button>
      ) : null}
    </p>
  )
}
